import React, {useContext, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import styles from "./index.module.scss";
import {Col, Container, Nav, Navbar, Offcanvas, Row} from "react-bootstrap";
import clsx from "clsx";
import InfoSection from "./components/InfoSection";
import {ReactComponent as Cap} from '../../svg/cap.svg';
import {ReactComponent as Pc} from '../../svg/pc_wo_mon.svg';
import TypedWrapper from "../../TypedWrapper";
import {displayElementInView, getBrowserLocales, observe} from "../../helpers";
import Doc from "../../models/Doc";
import Technologies from "../../data/Technologies";
import Monitor from "../../effects/monitor";
import localizer from "../../localizer";
import {useTranslation} from "react-i18next";
import i18next from "i18next";
import {BrowserLocalesContext} from "../../BrowserLocalesProvider";

const Writer = React.memo((props: {text: string}) => <TypedWrapper
    text={props.text}
    speed={100}
    className={clsx(styles.greeting_bottom)}
/>, (x, y) => x.text == y.text);

function MainPage() {

    const browserLocales = useContext(BrowserLocalesContext);

    const showToRussianSwitch =
        useMemo(() => {return browserLocales.includes('ru') && i18next.language != 'ru'}, [browserLocales, i18next.language]);
    const showToEnglishSwitch =
        useMemo(() => {return browserLocales.includes('en') && i18next.language != 'en'}, [browserLocales, i18next.language]);

    const {t} = useTranslation();

    const [showMenu, setShowMenu] = useState(false);
    const [resumeUrl, setResumeUrl] = useState("");

    const goToSection = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
        e.preventDefault();
        e.stopPropagation();

        const menuWasShown = showMenu;

        if(menuWasShown){
            setShowMenu(false);
        }

        const sectionId = e.currentTarget.attributes.getNamedItem("href")?.value;
        if(sectionId == null){
            return;
        }

        const el = document.getElementById(sectionId.substring(1));
        if(el == null){
            return;
        }

        const getYCoord = (el: HTMLElement) => {
            return el.getClientRects()[0].y;
        };

        if(menuWasShown){
            setTimeout(() => {
                //console.log(getYCoord(el))
                window.scrollTo(0, getYCoord(el));
                //el.scrollIntoView({behavior: "smooth", block: "start", inline: "nearest"});
            }, 500);
        }else{

            const yCoord = getYCoord(el);

            //console.log(yCoord)
            window.scrollTo(0, yCoord);
            //el.scrollIntoView({behavior: "smooth", block: "start", inline: "nearest"});
        }
    };

    const wr = useMemo(() => {
        return <Writer text={t('introduction')} />
    }, [i18next.language])

    const experienceTitles = ['amdocs', 'isdk', 'stecpoint', 's4ab', 'freelance', 'edu'];

    const [experiences, setExperiences] = useState<Doc[]>([]);
    const [edu, setEdu] = useState<Doc>();

    const getDocsAsync = async (localeName: string) => {
        const mdFileInfos = experienceTitles.map(x => ({name: x, path: require(`../../data/experiences/${x}.${localeName}.md`)}));
        const mdFiles = await Promise.all(
            mdFileInfos
                .map(async x => { return { name: x.name, text: await (await fetch(x.path)).text()} }));
        return mdFiles.map(x => new Doc(
            x.name,
            x.text,
            Technologies,
            { one: t('months.one'), two: t('months.two'), few: t('months.few') },
            { one: t('years.one'), two: t('years.two'), few: t('years.few') }
        ));
    };

    useEffect(() => {

        const newResumeName = 
            i18next.language == "ru" ? 
                "NIKOLAI MAEV RESUME RU.pdf" : "NIKOLAI MAEV RESUME.pdf"; 
        setResumeUrl(encodeURI(newResumeName));
        
        getDocsAsync(i18next.language)
            .then(x => {
                setExperiences(x.filter(y => y.name != 'edu'));
                setEdu(x.find(y => y.name == 'edu'));
            });

    }, [i18next.language]);

    const capRef = useRef<SVGSVGElement>(null);

    useLayoutEffect(() => {
        observe(document.body as HTMLElement);
    }, []);

    useLayoutEffect(() => {
        if(capRef.current == null){
            return;
        }
        displayElementInView(capRef.current as unknown as HTMLElement, (entry) => {
            const displayed = 'displayElementInView';
            if(!entry.target.classList.contains(displayed)){
                entry.target.innerHTML += "<style>" +
                    "@keyframes lcimgRrn_draw{to{stroke-dashoffset:0}}@keyframes lcimgRrn_fade{0%,94.44444444444444%{stroke-opacity:1}to{stroke-opacity:0}}" +
                    "</style>";

                entry.target.classList.add(displayed);
            }
        });
    }, [capRef.current]);

    return (

        <>

            <Container fluid className={clsx('w-100', 'min-vh-100', styles.topContainer_bg1)}>


                <Container fluid className={clsx('w-100', 'min-vh-100', styles.topContainer)}>

                    <Navbar expand={'sm'} collapseOnSelect className={styles.navbar}>

                        <div className='d-flex align-items-end justify-content-end w-100'>
                            <Navbar.Toggle
                                aria-controls={`offcanvasNavbar`}
                                onClick={() => setShowMenu((show) => !show)}
                            />
                        </div>

                        <Navbar.Collapse
                            id={`offcanvasNavbar`}
                            aria-labelledby={`offcanvasNavbarLabel`}
                        >
                            <Nav className={clsx(styles.topMenu, 'ms-auto')}>
                                <Nav.Link href="#experiences" onClick={e => goToSection(e)}>{t('experiences')}</Nav.Link>
                                <Nav.Link href="#education" onClick={e => goToSection(e)}>{t('education')}</Nav.Link>
                                {/*{showToRussianSwitch && <Nav.Link style={{minWidth: '100px'}} href='#' onClick={e => window.location.search = '?ru'}>To Russian</Nav.Link>}*/}
                                {/*{showToEnglishSwitch && <Nav.Link style={{minWidth: '100px'}} href='#' onClick={e => window.location.search = '?en'}>To English</Nav.Link>}*/}
                            </Nav>
                        </Navbar.Collapse>


                    </Navbar>

                    <Row>
                        <Col className="min-vh-100 position-relative d-flex flex-column">

                            <Row>
                                <Col md={5} sm={6} className='pt-lg-5 pt-md-5 pt-0'>

                                    {/*<div className={styles.shape1} />*/}
                                    {/*<div className={styles.shape2} />*/}

                                    <Row>
                                        <Col className={clsx(styles.greeting, 'p-xs-5 p-sm-3 p-lg-5')}>
                                            <div className={clsx(styles.greeting_top, 'pt-sm-0')}><span className={styles.greeting_top1}>{t('greetings')}</span><span className={clsx(styles.greeting_top2, 'observer animate_delay125')}>{t('name')}</span>

                                            </div>

                                            <div className={clsx('observer animate_delay125 mt-4')}>
                                                {/*<TypedWrapper*/}
                                                {/*    text={t('introduction')}*/}
                                                {/*    speed={100}*/}
                                                {/*    className={clsx(styles.greeting_bottom)}*/}
                                                {/*/>*/}
                                                {wr}
                                            </div>

                                            {/*<div className={clsx(styles.greeting_bottom, 'mt-4')}>I AM<br/> A FULL STACK<br/> SOFTWARE ENGINEER*/}
                                            {/*</div>*/}
                                        </Col>
                                    </Row>

                                </Col>

                                <Col md={7} sm={6} className='pt-lg-5 pt-sm-5'>
                                    <div
                                        className={`observer d-flex flex-column justify-content-center align-items-center ${clsx(styles.pcWrapper)}`}>

                                        <Monitor />

                                    </div>
                                    {/*<Cube></Cube>*/}
                                </Col>
                            </Row>

                            <Row className={`observer animate_delay125 ml-md-4 mx-lg-4 mx-xl-4 mt-md-1 mt-2 mb-lg-2 ${clsx(styles.topButtons)}`}>
                                <Col md={2} sm={3}>
                                    <a target='_blank' href={resumeUrl}>{t('myResume')}</a>
                                </Col>
                                <Col>
                                    <a href='mailto:trix.mnr@gmail.com'>{t('emailMe')}</a>
                                </Col>
                            </Row>

                            <Row>
                                <Col></Col>
                            </Row>

                            <Row className={`observer animate_delay125 sharedLinks ml-md-2 mx-lg-3 mx-xl-3 mt-md-5 mt-3 mb-3 mt-sm-5 ${clsx(styles.sharedLinks)}`}>
                                {i18next.language != "ru" && <Col>
                                    <a className='hover-underline-animation' href='https://www.linkedin.com/in/sharpdev/' target='_blank'>
                                        <i className='linkedin' />
                                        LinkedIn
                                    </a>
                                </Col>}
                                <Col>
                                    <a className='hover-underline-animation' href='https://github.com/NickMaev' target='_blank'>
                                        <i className='github' />
                                        GitHub
                                    </a>
                                </Col>
                                <Col>
                                    <a className='hover-underline-animation' href='skype:trix.mnr?chat' target='_blank'>
                                        <i className='skype' />
                                        Skype
                                    </a>
                                </Col>
                                <Col>
                                    <a className='hover-underline-animation' href='https://t.me/trixon' target='_blank'>
                                        <i className='telegram' />
                                        Telegram
                                    </a>
                                </Col>
                                {/*<Col>*/}
                                {/*    <a className='hover-underline-animation' href='mailto:trix.mnr@gmail.com' target='_blank'>*/}
                                {/*        <i className='email' />*/}
                                {/*        Email*/}
                                {/*    </a>*/}
                                {/*</Col>*/}
                            </Row>

                        </Col>
                    </Row>



                </Container>

            </Container>

            <div className={styles.experiencesWrapper}>
                <Container id='experiences' className={clsx('p-3 py-lg-5 py-sm-4 py-md-4')}>
                    <h2>{t('experiences')}</h2>
                    {experiences.map((x, i) =>
                        <InfoSection key={i} mdText={x.getTransformedText()}/>)}
                </Container>
            </div>

            <Container fluid id='education' className={clsx('py-5', styles.educationWrapper)}>

                <Container>
                    <InfoSection mdText={edu?.getTransformedText()}>
                        <div className='d-flex flex-column justify-content-center align-items-center'>
                            <Cap ref={capRef} className={clsx(styles.centeredSvg)}/>
                        </div>
                    </InfoSection>
                </Container>


            </Container>

        </>
    );
}

export default MainPage;